import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout, Button, Radio, Select } from 'antd';
import { 
  ArrowLeftOutlined
} from '@ant-design/icons';
import './IntroPage.css';
import './DemographicPage.css';

const { Content } = Layout;

const DemographicPage = () => {
  const navigate = useNavigate();
  const { type } = useParams();
  
  const [formData, setFormData] = useState({
    age: null,
    gender: null,
    zodiac: null,
    relationshipStatus: null,
    relationshipDuration: null
  });

  const testTypeText = type === 'self' ? '给自己测' : '为恋人测';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // 检查sessionId
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      navigate('/');
      return;
    }

    // 检查是否已完成该类型的测试
    const completed = localStorage.getItem(`rpi_completed_${type}`);
    if (completed === 'true') {
      navigate(`/report/${type}`);
    }
  }, [type, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    // 检查必填项
    if (!formData.age || !formData.gender || !formData.zodiac || !formData.relationshipStatus) {
      alert('请填写所有必填项（标有*的项目）');
      return;
    }
    
    // 保存基本信息到localStorage
    const sessionId = localStorage.getItem('sessionId');
    localStorage.setItem(`rpi_demographic_${type}_${sessionId}`, JSON.stringify(formData));
    
    // 跳转到问卷
    navigate(`/questionnaire/${type}`);
  };

  const isFormValid = formData.age && formData.gender && formData.zodiac && formData.relationshipStatus;

  return (
    <Layout className="rpi-intro-layout">
      <header className="rpi-header">
        <div className="rpi-header-content">
          <div className="rpi-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <div className="rpi-logo-icon">💕</div>
            <div className="rpi-logo-text">
              <div className="rpi-logo-title">RPI Calculator</div>
              <div className="rpi-logo-subtitle">恋爱占有欲指数计算器</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--rpi-primary)' }}>
              {type === 'self' ? '💖' : '💞'} {testTypeText}
            </span>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(-1)}
            >
              返回
            </Button>
          </div>
        </div>
      </header>

      <Content>
        <div className="rpi-demographic-container">
          {/* Hero */}
          <div className="rpi-demographic-hero">
            <div className="rpi-demographic-hero-icon">👤</div>
            <h1 className="rpi-demographic-hero-title">
              {type === 'self' ? '基本信息' : '恋人基本信息'}
            </h1>
            <p className="rpi-demographic-hero-desc">
              {type === 'self' 
                ? '请提供一些基本信息，这将帮助我们提供更准确的结果分析'
                : '请提供恋人的基本信息，这将帮助我们提供更准确的评估结果'
              }
            </p>
          </div>

          {/* 表单卡片 */}
          <div className="rpi-demographic-form-card">
            {/* 年龄段 */}
            <div className="rpi-demographic-form-item">
              <div className="rpi-demographic-form-label">
                <span className="rpi-demographic-label-icon">🎂</span>
                <span className="rpi-demographic-label-text">
                  {type === 'self' ? '您的年龄段' : 'Ta的年龄段'}
                </span>
                <span className="rpi-demographic-required">*</span>
              </div>
              <Radio.Group 
                value={formData.age}
                onChange={(e) => handleChange('age', e.target.value)}
                className="rpi-demographic-radio-group"
              >
                <Radio value="under18" className="rpi-demographic-radio">18岁以下</Radio>
                <Radio value="18-24" className="rpi-demographic-radio">18-24岁</Radio>
                <Radio value="25-29" className="rpi-demographic-radio">25-29岁</Radio>
                <Radio value="30-34" className="rpi-demographic-radio">30-34岁</Radio>
                <Radio value="35-39" className="rpi-demographic-radio">35-39岁</Radio>
                <Radio value="40+" className="rpi-demographic-radio">40岁以上</Radio>
              </Radio.Group>
            </div>

            {/* 性别 */}
            <div className="rpi-demographic-form-item">
              <div className="rpi-demographic-form-label">
                <span className="rpi-demographic-label-icon">⚧️</span>
                <span className="rpi-demographic-label-text">
                  {type === 'self' ? '您的性别' : 'Ta的性别'}
                </span>
                <span className="rpi-demographic-required">*</span>
              </div>
              <Radio.Group 
                value={formData.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                className="rpi-demographic-radio-group"
              >
                <Radio value="male" className="rpi-demographic-radio">男性</Radio>
                <Radio value="female" className="rpi-demographic-radio">女性</Radio>
                <Radio value="other" className="rpi-demographic-radio">其他</Radio>
                <Radio value="prefer-not-say" className="rpi-demographic-radio">不愿透露</Radio>
              </Radio.Group>
            </div>

            {/* 星座 */}
            <div className="rpi-demographic-form-item">
              <div className="rpi-demographic-form-label">
                <span className="rpi-demographic-label-icon">⭐</span>
                <span className="rpi-demographic-label-text">
                  {type === 'self' ? '您的星座' : 'Ta的星座'}
                </span>
                <span className="rpi-demographic-required">*</span>
              </div>
              <Select
                value={formData.zodiac}
                onChange={(value) => handleChange('zodiac', value)}
                placeholder="请选择星座"
                className="rpi-demographic-select"
                size="large"
                style={{ width: '100%' }}
              >
                <Select.Option value="aries">白羊座 ♈</Select.Option>
                <Select.Option value="taurus">金牛座 ♉</Select.Option>
                <Select.Option value="gemini">双子座 ♊</Select.Option>
                <Select.Option value="cancer">巨蟹座 ♋</Select.Option>
                <Select.Option value="leo">狮子座 ♌</Select.Option>
                <Select.Option value="virgo">处女座 ♍</Select.Option>
                <Select.Option value="libra">天秤座 ♎</Select.Option>
                <Select.Option value="scorpio">天蝎座 ♏</Select.Option>
                <Select.Option value="sagittarius">射手座 ♐</Select.Option>
                <Select.Option value="capricorn">摩羯座 ♑</Select.Option>
                <Select.Option value="aquarius">水瓶座 ♒</Select.Option>
                <Select.Option value="pisces">双鱼座 ♓</Select.Option>
              </Select>
            </div>

            {/* 恋爱状态 */}
            <div className="rpi-demographic-form-item">
              <div className="rpi-demographic-form-label">
                <span className="rpi-demographic-label-icon">💑</span>
                <span className="rpi-demographic-label-text">
                  {type === 'self' ? '您目前的恋爱状态' : '你们目前的恋爱状态'}
                </span>
                <span className="rpi-demographic-required">*</span>
              </div>
              <Radio.Group 
                value={formData.relationshipStatus}
                onChange={(e) => handleChange('relationshipStatus', e.target.value)}
                className="rpi-demographic-radio-group rpi-demographic-radio-group-grid"
              >
                <Radio value="single-never" className="rpi-demographic-radio">单身（从未恋爱）</Radio>
                <Radio value="single-experienced" className="rpi-demographic-radio">单身（单段或多段）</Radio>
                <Radio value="honeymoon" className="rpi-demographic-radio">热恋期</Radio>
                <Radio value="stable" className="rpi-demographic-radio">平淡期</Radio>
                <Radio value="adjustment" className="rpi-demographic-radio">磨合期</Radio>
                <Radio value="frequent-quarrels" className="rpi-demographic-radio">三天两吵</Radio>
                <Radio value="cold-war" className="rpi-demographic-radio">冷战期</Radio>
                <Radio value="engaged-married" className="rpi-demographic-radio">已订婚/结婚</Radio>
                <Radio value="long-distance" className="rpi-demographic-radio">异地恋</Radio>
              </Radio.Group>
            </div>

            {/* 恋爱持续时间 */}
            <div className="rpi-demographic-form-item">
              <div className="rpi-demographic-form-label">
                <span className="rpi-demographic-label-icon">⏱️</span>
                <span className="rpi-demographic-label-text">
                  {type === 'self' ? '当前(最近)一段恋爱的持续时间' : '你们在一起的时间'}
                </span>
              </div>
              <Radio.Group 
                value={formData.relationshipDuration}
                onChange={(e) => handleChange('relationshipDuration', e.target.value)}
                className="rpi-demographic-radio-group"
              >
                <Radio value="never" className="rpi-demographic-radio">从未恋爱</Radio>
                <Radio value="less-3m" className="rpi-demographic-radio">少于3个月</Radio>
                <Radio value="3-6m" className="rpi-demographic-radio">3-6个月</Radio>
                <Radio value="6m-1y" className="rpi-demographic-radio">6个月-1年</Radio>
                <Radio value="1-2y" className="rpi-demographic-radio">1-2年</Radio>
                <Radio value="2-3y" className="rpi-demographic-radio">2-3年</Radio>
                <Radio value="3y+" className="rpi-demographic-radio">3年以上</Radio>
              </Radio.Group>
            </div>
          </div>

          {/* 提示信息 */}
          <div className="rpi-demographic-tip">
            <span className="rpi-demographic-tip-icon">💡</span>
            <p>
              这些信息将帮助我们提供更个性化的分析结果。所有信息仅在您的设备本地存储，完全匿名且安全。
            </p>
          </div>

          {/* 底部按钮 */}
          <div className="rpi-demographic-actions">
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              disabled={!isFormValid}
              className="rpi-demographic-btn-submit"
            >
              下一步，开始答题
            </Button>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default DemographicPage;
